import { AlertTriangle, Download } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/schema";
import { EmptyState } from "@/shared/components/EmptyState";
import { KpiCard } from "@/shared/components/KpiCard";
import { cn } from "@/shared/lib/cn";
import { Skeleton } from "@/shared/ui/skeleton";

import { usePerformanceExport } from "../api/use-performance-export";
import { usePromotionPerformance } from "../api/use-promotion-performance";
import {
  DASH,
  bannerFor,
  exportFilename,
  formatAsOf,
  formatClock,
  formatMoney,
  giftsCaption,
  kpiSubLabel,
  periodInfo,
  upliftDisplay,
} from "../lib/performance";

import { SecondaryButton } from "./buttons";
import { BrandTable, GiftsTable, TopSkusTable } from "./PerformanceTables";
import { QuotaProgressBar } from "./QuotaProgressBar";

type Promotion = components["schemas"]["Promotion"];

// items-start: tiles keep their own height instead of stretching to the taller gifts tile.
// Stacked 1-up below `sm` (phone widths): a 2-up row there left each card too narrow for a
// 7-figure baht value under KPI_VALUE's nowrap (text overran the card edge). From `sm` it's
// the original 2-up; the gifts tile spans that 2-up row so it doesn't sit alone at half width.
const KPI_GRID =
  "grid grid-cols-1 items-start gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 sm:[&>*:last-child]:col-span-2 md:[&>*:last-child]:col-span-1";
// One size for every KPI value (shared baseline). A 13-char baht figure ("฿1,239,983.00") is
// ~165px at text-2xl; 5-up tiles only have that much inner width from ~1400px, so 1280–1399
// steps down to text-xl rather than spill (nowrap keeps a number on one line).
const KPI_VALUE = "block whitespace-nowrap text-lg md:text-2xl xl:text-xl min-[1400px]:text-2xl";
const CARD =
  "rounded-[14px] border border-[var(--premium-border)] bg-[var(--surface-raised)] shadow-[var(--sh-card)]";

interface PerformancePanelProps {
  promotion: Promotion;
}

/** Performance tab (spec §5.2): a snapshot report — no polling, no motion. */
export function PerformancePanel({ promotion }: PerformancePanelProps): JSX.Element {
  const { t } = useTranslation("promotion");
  const perf = usePromotionPerformance(promotion.id);
  const exporter = usePerformanceExport(promotion.id);
  const { status } = promotion;

  if (perf.isPending) return <PerformanceSkeleton />;
  if (perf.isError) {
    return (
      <div className={CARD} data-testid="perf-error">
        <EmptyState
          icon={<AlertTriangle className="h-5 w-5" aria-hidden />}
          title={t("performance.error")}
          action={
            <SecondaryButton data-testid="perf-retry" onClick={() => void perf.refetch()}>
              {t("performance.retry")}
            </SecondaryButton>
          }
        />
      </div>
    );
  }

  const d = perf.data;
  const empty = d.orders === 0;
  const { daysLeft } = periodInfo(promotion.start_at, promotion.end_at, new Date());
  const banner = bannerFor(status, daysLeft, d.in_transit_orders);
  const uplift = upliftDisplay(d.abv_uplift_pct, d.baseline_abv);
  const gifts = giftsCaption(status, d.gifts);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-[var(--text-subtle)]" data-testid="perf-as-of">
          {t("performance.as_of", { time: formatAsOf(d.as_of) })}
        </span>
        <SecondaryButton
          className="ml-auto"
          data-testid="perf-export"
          disabled={exporter.isPending}
          onClick={() => exporter.mutate(exportFilename(promotion.name, status, new Date()))}
        >
          <Download className="h-4 w-4" aria-hidden />
          {t("performance.export")}
        </SecondaryButton>
      </div>

      {banner && (
        <div
          role="status"
          data-testid="perf-banner"
          data-intent={banner.intent}
          className={cn(
            "rounded-[var(--control-r)] px-4 py-2.5 text-[13px] font-medium",
            banner.intent === "warning"
              ? "bg-[var(--intent-warning-bg)] text-[var(--intent-warning-fg)]"
              : "bg-[var(--intent-info-bg)] text-[var(--intent-info-fg)]",
          )}
        >
          {t(banner.key, { ...banner.values, time: formatClock(d.as_of) })}
        </div>
      )}

      <div className={KPI_GRID} data-testid="perf-kpis">
        <KpiCard label={t("performance.kpi.orders")} caption={t(kpiSubLabel(status, "orders"))}>
          <span className={KPI_VALUE} data-testid="perf-kpi-orders">
            {empty ? DASH : d.orders.toLocaleString("en-US")}
          </span>
        </KpiCard>
        <KpiCard label={t("performance.kpi.gmv")} caption={t(kpiSubLabel(status, "gmv"))}>
          <span className={KPI_VALUE} data-testid="perf-kpi-gmv">
            {empty ? DASH : formatMoney(d.gmv)}
          </span>
        </KpiCard>
        <KpiCard label={t("performance.kpi.abv")} caption={t(kpiSubLabel(status, "abv"))}>
          <span className={KPI_VALUE} data-testid="perf-kpi-abv">
            {empty ? DASH : formatMoney(d.abv)}
          </span>
        </KpiCard>
        <KpiCard
          label={t("performance.kpi.uplift")}
          caption={t(uplift.caption.key, uplift.caption.values)}
        >
          <span className={KPI_VALUE} data-testid="perf-kpi-uplift">
            {uplift.value}
          </span>
        </KpiCard>
        <KpiCard
          label={t("performance.kpi.gifts")}
          {...(gifts ? { caption: t(gifts.key, gifts.values) } : {})}
        >
          <div className="flex flex-col gap-2">
            <span className={KPI_VALUE} data-testid="perf-kpi-gifts">
              {empty ? DASH : d.gifts.distributed.toLocaleString("en-US")}
            </span>
            <QuotaProgressBar used={d.gifts.quota_used} total={d.gifts.quota_total} />
          </div>
        </KpiCard>
      </div>

      <BrandTable data={d} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <GiftsTable data={d} />
        <TopSkusTable data={d} />
      </div>
    </div>
  );
}

function PerformanceSkeleton(): JSX.Element {
  return (
    <div className="space-y-6" data-testid="perf-loading">
      <div className={KPI_GRID}>
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-[120px] rounded-[14px]" />
        ))}
      </div>
      <div className={`${CARD} space-y-2 p-4`}>
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-9" />
        ))}
      </div>
    </div>
  );
}
